import { forwardRef, ReactNode } from 'react';
import type { CSSProperties, HTMLAttributes, KeyboardEvent } from 'react';
import { Button, Space } from 'antd';
import {
    TbFocusCentered,
    TbLayoutSidebarRightCollapse,
    TbLayoutSidebarRightExpand,
    TbMinus,
    TbPlus,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

interface DiagramCanvasSurfaceProps {
    'aria-label'?: string;
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
}

interface DiagramViewportProps {
    children: ReactNode;
    style?: CSSProperties;
}

interface DiagramNodeHandleProps {
    active?: boolean;
    position: 'bottom' | 'top';
}

interface DiagramNodeSurfaceProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
    active?: boolean;
    ariaLabel: string;
    children: ReactNode;
    onSelect: () => void;
    selected?: boolean;
}

interface DiagramEdgePathProps {
    active?: boolean;
    path: string;
}

interface DiagramZoomControlsProps {
    inspectorLabel?: string;
    inspectorOpen?: boolean;
    maximumZoom?: number;
    minimumZoom?: number;
    onFit?: () => void;
    onInspectorOpenChange?: (open: boolean) => void;
    onZoomChange: (zoom: number) => void;
    style?: CSSProperties;
    zoom: number;
    zoomStep?: number;
}

interface DiagramDetailSiderProps {
    children: ReactNode;
    open: boolean;
    style?: CSSProperties;
    width?: CSSProperties['width'];
}

export const DiagramCanvasSurface = forwardRef<HTMLDivElement, DiagramCanvasSurfaceProps>(
    ({ children, className, style, ...ariaProps }, ref) => {
        const { token } = useAntDesignTheme();

        return (
            <div
                {...ariaProps}
                ref={ref}
                className={className}
                style={{
                    backgroundColor: token.colorBgBase,
                    backgroundImage: `radial-gradient(circle at 0.125rem 0.125rem, ${token.colorBorder} 0.125rem, transparent 0)`,
                    backgroundSize: '1.5rem 1.5rem',
                    height: '100%',
                    minHeight: 0,
                    overflow: 'auto',
                    position: 'relative',
                    ...style,
                }}
            >
                {children}
            </div>
        );
    },
);

DiagramCanvasSurface.displayName = 'DiagramCanvasSurface';

export const DiagramViewport = ({ children, style }: DiagramViewportProps): JSX.Element => (
    <div
        style={{
            height: '100%',
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
            position: 'relative',
            ...style,
        }}
    >
        {children}
    </div>
);

export const DiagramNodeHandle = ({ active = false, position }: DiagramNodeHandleProps): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <span
            aria-hidden
            style={{
                alignItems: 'center',
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${active ? token.colorPrimary : token.colorBorder}`,
                borderRadius: '50%',
                boxShadow: `0 0 0 2px ${token.colorBgContainer}`,
                boxSizing: 'border-box',
                display: 'inline-flex',
                height: '0.75rem',
                justifyContent: 'center',
                left: '50%',
                position: 'absolute',
                transform: 'translateX(-50%)',
                width: '0.75rem',
                zIndex: 2,
                [position]: '-0.375rem',
            }}
        >
            <span
                style={{
                    backgroundColor: active ? token.colorPrimary : token.colorBorder,
                    borderRadius: '50%',
                    height: '0.25rem',
                    width: '0.25rem',
                }}
            />
        </span>
    );
};

export const DiagramNodeSurface = forwardRef<HTMLDivElement, DiagramNodeSurfaceProps>(
    ({ active = false, ariaLabel, children, onKeyDown, onSelect, selected = false, style, ...props }, ref) => {
        const { token } = useAntDesignTheme();
        const highlighted = active || selected;
        const depthShadow = `0 0.125rem 0.4rem color-mix(in srgb, ${token.colorBgMask} 12%, transparent)`;
        const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
            onKeyDown?.(event);
            if (event.defaultPrevented) {
                return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect();
            }
        };

        return (
            <div
                {...props}
                ref={ref}
                aria-label={ariaLabel}
                aria-pressed={selected}
                role='button'
                tabIndex={0}
                style={{
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${highlighted ? token.colorPrimaryBorder : token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    boxShadow: highlighted ? `0 0 0 2px ${token.colorPrimaryBg}, ${depthShadow}` : depthShadow,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'border-color 140ms ease, box-shadow 140ms ease',
                    width: '100%',
                    ...style,
                }}
                onClick={onSelect}
                onKeyDown={handleKeyDown}
            >
                <DiagramNodeHandle active={highlighted} position='top' />
                {children}
                <DiagramNodeHandle active={highlighted} position='bottom' />
            </div>
        );
    },
);

DiagramNodeSurface.displayName = 'DiagramNodeSurface';

export const DiagramEdgePath = ({ active = false, path }: DiagramEdgePathProps): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <path
            d={path}
            fill='none'
            stroke={
                active ? token.colorPrimary : `color-mix(in srgb, ${token.colorTextTertiary} 48%, ${token.colorBorder})`
            }
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='1.25'
            vectorEffect='non-scaling-stroke'
        />
    );
};

export const DiagramZoomControls = ({
    inspectorLabel = 'diagram details',
    inspectorOpen,
    maximumZoom = 1.4,
    minimumZoom = 0.5,
    onFit,
    onInspectorOpenChange,
    onZoomChange,
    style,
    zoom,
    zoomStep = 0.1,
}: DiagramZoomControlsProps): JSX.Element => {
    const { token } = useAntDesignTheme();
    const roundedZoom = (nextZoom: number) => Number(nextZoom.toFixed(2));

    return (
        <Space.Compact
            direction='vertical'
            style={{ pointerEvents: 'auto', position: 'absolute', right: '1rem', top: '1rem', zIndex: 6, ...style }}
        >
            <Button
                aria-label='Zoom in'
                disabled={zoom >= maximumZoom}
                icon={<TbPlus />}
                size='small'
                onClick={() => onZoomChange(Math.min(maximumZoom, roundedZoom(zoom + zoomStep)))}
            />
            <Button
                aria-label='Zoom out'
                disabled={zoom <= minimumZoom}
                icon={<TbMinus />}
                size='small'
                onClick={() => onZoomChange(Math.max(minimumZoom, roundedZoom(zoom - zoomStep)))}
            />
            {onFit ? <Button aria-label='Fit diagram' icon={<TbFocusCentered />} size='small' onClick={onFit} /> : null}
            {onInspectorOpenChange && inspectorOpen !== undefined ? (
                <Button
                    aria-label={`${inspectorOpen ? 'Collapse' : 'Expand'} ${inspectorLabel}`}
                    icon={
                        inspectorOpen ? (
                            <TbLayoutSidebarRightCollapse style={{ color: token.colorTextTertiary }} />
                        ) : (
                            <TbLayoutSidebarRightExpand style={{ color: token.colorTextTertiary }} />
                        )
                    }
                    size='small'
                    onClick={() => onInspectorOpenChange(!inspectorOpen)}
                />
            ) : null}
        </Space.Compact>
    );
};

export const DiagramDetailSider = ({
    children,
    open,
    style,
    width = '15rem',
}: DiagramDetailSiderProps): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <div
            aria-hidden={!open}
            style={{
                backgroundColor: token.colorBgBase,
                borderLeft: open ? `1px solid ${token.colorBorderSecondary}` : '0 solid transparent',
                flex: open ? `0 0 ${width}` : '0 0 0',
                minHeight: 0,
                minWidth: open ? width : 0,
                opacity: open ? 1 : 0,
                overflow: 'hidden',
                pointerEvents: open ? 'auto' : 'none',
                transition: 'flex-basis 180ms ease, min-width 180ms ease, opacity 140ms ease, border-width 180ms ease',
                ...style,
            }}
        >
            <div style={{ height: '100%', width }}>{children}</div>
        </div>
    );
};
